import PropTypes from 'prop-types';

import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';
import { ReactstrapButton as Button } from '@febos-io/design';
import { useDispatch, useSelector } from 'react-redux';
import { handleContentWidth, handleMenuCollapsed, handleMenuHidden } from '../../store/layout';
// ** Custom Components
import Customizer from '../components/customizer';
import ScrollToTop from '../components/scrolltop';
import FooterComponent from './components/Footer';
import NavbarComponent from './components/navbar/ThemeNavbar';
import Sidebar from './components/menu/vertical-menu/Sidebar';

// ** Custom Hooks
import { useRTL } from '../../utility/hooks/useRTL';
import { useSkin } from '../../utility/hooks/useSkin';
import { useLayout } from '../../utility/hooks/useLayout';
import { useNavbarType } from '../../utility/hooks/useNavbarType';
import { useFooterType } from '../../utility/hooks/useFooterType';
import { useNavbarColor } from '../../utility/hooks/useNavbarColor';

// ** Styles
import '../scss/base/core/menu/menu-types/vertical-menu.scss';
import '../scss/base/core/menu/menu-types/vertical-overlay-menu.scss';
import 'react-perfect-scrollbar/dist/css/styles.css';

import Icon from '../../../components/Icon';
import { useThemeContext } from '../../utility/context/ThemeColors';

// Simple Navbar component to replace reactstrap Navbar
// Replicates the exact HTML structure and classes that reactstrap generates
const Navbar = ({ children, className, light, dark, color, expand, container, style, ...props }) => {
  // Build classes exactly as reactstrap does
  const classes = classnames(
    'navbar',
    {
      'navbar-light': light,
      'navbar-dark': dark,
      [`bg-${color}`]: color,
      [`navbar-expand-${expand}`]: expand
    },
    className
  );
  
  return (
    <nav 
      className={classes}
      {...props}
    >
      {children}
    </nav>
  );
};

function VerticalLayout({
  menu,
  navbar,
  footer,
  children,
  menuData
}) {
  const { themeConfig } = useThemeContext();
  // ** Hooks
  const [isRtl, setIsRtl] = useRTL();
  const { skin, setSkin } = useSkin();
  const { navbarType, setNavbarType } = useNavbarType();
  const { footerType, setFooterType } = useFooterType();
  const { navbarColor, setNavbarColor } = useNavbarColor();
  const { layout, setLayout, setLastLayout } = useLayout();

  // ** States
  const [isMounted, setIsMounted] = useState(false);
  const [menuVisibility, setMenuVisibility] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // ** Vars
  const dispatch = useDispatch();
  const layoutStore = useSelector(state => state.layout);

  // ** Update Window Width
  const handleWindowWidth = () => {
    setWindowWidth(window.innerWidth);
  };

  // ** Vars
  const location = useLocation();
  const isHidden = layoutStore.menuHidden || themeConfig.layout.menu.isHidden;
  const contentWidth = layoutStore.contentWidth;
  const menuCollapsed = layoutStore.menuCollapsed;

  // ** Toggles Menu Collapsed
  const setMenuCollapsed = val => dispatch(handleMenuCollapsed(val));

  // ** Handles Content Width
  const setContentWidth = val => dispatch(handleContentWidth(val));

  // ** Handles Content Width
  const setIsHidden = val => dispatch(handleMenuHidden(val));

  //* * This function will detect the Route Change and will hide the menu on menu item click
  // TODO: hide menu on router change.
  useEffect(() => {
    if (menuVisibility && windowWidth < 1200) {
      setMenuVisibility(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, windowWidth]);

  //* * Sets Window Size & Layout Props
  useEffect(() => {
    if (window !== undefined) {
      window.addEventListener('resize', handleWindowWidth);
    }
  }, [windowWidth]);

  //* * ComponentDidMount
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // ** Vars
  const footerClasses = {
    static: 'footer-static',
    sticky: 'footer-fixed',
    hidden: 'footer-hidden'
  };

  const navbarWrapperClasses = {
    floating: 'navbar-floating',
    sticky: 'navbar-sticky',
    static: 'navbar-static',
    hidden: 'navbar-hidden'
  };

  const navbarClasses = {
    floating: contentWidth === 'boxed' ? 'floating-nav container-xxl' : 'floating-nav',
    sticky: 'fixed-top',
    static: 'navbar-static-top',
    hidden: 'd-none'
  };

  const bgColorCondition = navbarColor !== '' && navbarColor !== 'light' && navbarColor !== 'white';

  if (!isMounted) {
    return null;
  }
  return (
    <div
      className={classnames(
        `wrapper vertical-layout ${navbarWrapperClasses[navbarType] || 'navbar-floating'} ${
          footerClasses[footerType] || 'footer-static'
        }`,
        {
          // Modern Menu
          'vertical-menu-modern': windowWidth >= 1200,
          'menu-collapsed': menuCollapsed && windowWidth >= 1200,
          'menu-expanded': !menuCollapsed && windowWidth > 1200,

          // Overlay Menu
          'vertical-overlay-menu': windowWidth < 1200,
          'menu-hide': !menuVisibility && windowWidth < 1200,
          'menu-open': menuVisibility && windowWidth < 1200
        }
      )}
      {...(isHidden ? { 'data-col': '1-column' } : {})}
    >
      {!isHidden ? (
        <Sidebar
          skin={skin}
          menu={menu}
          menuData={menuData}
          menuCollapsed={menuCollapsed}
          menuVisibility={menuVisibility}
          setMenuCollapsed={setMenuCollapsed}
          setMenuVisibility={setMenuVisibility}
        />
      ) : null}

      <Navbar
        expand="lg"
        container={false}
        light={skin !== 'dark'}
        dark={skin === 'dark' || bgColorCondition}
        color={bgColorCondition ? navbarColor : undefined}
        className={classnames(
          `header-navbar navbar align-items-center ${navbarClasses[navbarType]
          || 'floating-nav'} navbar-shadow`
        )}
        style={{ minHeight: '62px', backgroundColor: skin === 'dark' ? '#283046' : '#fff', zIndex: 1000 }}
      >
        <div className="navbar-container d-flex content">
          {navbar ? (
            navbar({ skin, setSkin, setMenuVisibility })
          ) : (
            <NavbarComponent
              setMenuVisibility={setMenuVisibility}
              skin={skin}
              setSkin={setSkin}
            />
          )}
        </div>
      </Navbar>
      {children}

      <div
        className={classnames('sidenav-overlay', {
          show: menuVisibility
        })}
        onClick={() => setMenuVisibility(false)}
      />

      {themeConfig.layout.customizer === true ? (
        <Customizer
          skin={skin}
          isRtl={isRtl}
          layout={layout}
          setSkin={setSkin}
          setIsRtl={setIsRtl}
          isHidden={isHidden}
          setLayout={setLayout}
          footerType={footerType}
          navbarType={navbarType}
          setIsHidden={setIsHidden}
          themeConfig={themeConfig}
          navbarColor={navbarColor}
          contentWidth={contentWidth}
          setFooterType={setFooterType}
          setNavbarType={setNavbarType}
          setLastLayout={setLastLayout}
          menuCollapsed={menuCollapsed}
          setNavbarColor={setNavbarColor}
          setContentWidth={setContentWidth}
          setMenuCollapsed={setMenuCollapsed}
        />
      ) : null}
      <footer
        className={classnames(
          `footer footer-light ${footerClasses[footerType] || 'footer-static'}`,
          {
            'd-none': footerType === 'hidden'
          }
        )}
      >
        {footer || (
          <FooterComponent
            footerType={footerType}
            footerClasses={footerClasses}
          />
        )}
      </footer>

      {themeConfig.layout.scrollTop === true ? (
        <div className="scroll-to-top">
          <ScrollToTop
            showOffset={300}
            className="scroll-top d-block"
          >
            <Button
              className="btn-icon"
              color="primary"
            >
              <Icon
                icon="fa-arrow-up"
              />
            </Button>
          </ScrollToTop>
        </div>
      ) : null}
    </div>
  );
}

VerticalLayout.propTypes = {
  menu: PropTypes.any,
  navbar: PropTypes.any,
  footer: PropTypes.any,
  children: PropTypes.node,
  menuData: PropTypes.array
};
export default VerticalLayout;
